import { IconAlertCircle } from '@tabler/icons-react';
import type { StyledMantineProps } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/styledMantine.types';
import styled from 'styled-components';
import { useState } from 'react';
import { Box, Center, Group, Loader, Popover, Stack, Text, UnstyledButton } from '@mantine/core';
import type { CSSObject } from 'styled-components';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { tickIn } from '../../theme/dealstackAi.animations';
import {
	DS_AI_MOTION,
	DS_AI_POPOVER_PROPS,
	DS_AI_SUCCESS,
	DS_AI_TABULAR_FIGURES,
	dsAiFocusRingStyles,
} from '../../theme/dealstackAi.tokens';
import { PROCESSING_ICON_SIZE } from './ProcessingCard.styles';
import type { ProcessingStageView } from './ProcessingCard.types';
import { ProcessingSubstepItem } from './ProcessingSubstepItem';

const { colors } = defaultTheme.v3;

const HEADER_STYLES = {
	display: 'flex',
	alignItems: 'center',
	gap: '10px',
	border: 0,
	borderRadius: '4px',
	background: 'transparent',
	textAlign: 'left',
	color: 'inherit',
	cursor: 'pointer',
	'&:disabled': { cursor: 'default', opacity: 1 },
	...dsAiFocusRingStyles(2),
} satisfies CSSObject;

interface Props {
	stage: ProcessingStageView;
	expanded: boolean;
	failed?: boolean;
}

/** Every step keeps its slot and its count once it runs; completed details open without moving the checklist. */
export const ProcessingStageItem = ({ stage, expanded, failed = false }: Props) => {
	const t = useTranslationCapManage();
	const [opened, setOpened] = useState(false);
	const substeps = stage.substeps.filter((step) => step.state !== 'pending');
	const done = stage.state === 'done';
	const canOpen = done && !expanded && !failed;
	const count = stage.count === null ? null : t(stage.count.key, stage.count.values);
	const rate = stage.rate === null ? null : t(stage.rate.key, stage.rate.values);
	let icon = (
		<Box
			component="span"
			w="14px"
			h="14px"
			bd="1px solid gray.2"
			bg="white.0"
			style={{ borderRadius: '50%' }}
		/>
	);
	if (failed) {
		icon = <IconAlertCircle size={16} color="var(--mantine-color-red-6)" />;
	} else if (done) {
		icon = (
			<StageTick component="span" display="inline-flex" c={DS_AI_SUCCESS.text}>
				<Icon variant="tick-circle-filled" size="lg" />
			</StageTick>
		);
	} else if (stage.state === 'running') {
		icon = <Loader type="oval" size={16} color="blue.6" />;
	}
	const details = (
		<Stack component="ul" gap={6} m={0} p={0} style={{ listStyle: 'none' }}>
			{substeps.map((step) => (
				<ProcessingSubstepItem key={step.key} substep={step} />
			))}
		</Stack>
	);
	return (
		<StageConnector
			pos="relative"
			data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PROCESSING_STAGE}
			data-state={failed ? 'error' : stage.state}
		>
			<Popover
				{...DS_AI_POPOVER_PROPS}
				opened={canOpen && opened}
				onChange={setOpened}
				position="right-start"
				width={360}
				withinPortal
			>
				<Popover.Target>
					<UnstyledButton
						type="button"
						disabled={!canOpen}
						aria-expanded={canOpen && opened}
						onClick={() => setOpened(!opened)}
						onMouseEnter={() => setOpened(true)}
						onMouseLeave={() => setOpened(false)}
						onFocus={() => setOpened(true)}
						onBlur={() => setOpened(false)}
						w="100%"
						h={34}
						px={8}
						styles={{ root: HEADER_STYLES }}
					>
						<Center
							component="span"
							inline
							w={PROCESSING_ICON_SIZE}
							h={PROCESSING_ICON_SIZE}
							flex="none"
							aria-hidden
						>
							{icon}
						</Center>
						<Text
							span
							flex={1}
							miw={0}
							size="md"
							fw={600}
							c={expanded ? 'dark.8' : 'dark.0'}
						>
							{t(stage.titleKey)}
						</Text>
						{count && (
							<Text
								span
								fz="sm"
								c="dark.0"
								maw="45%"
								style={{
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}
								title={count}
							>
								{count}
							</Text>
						)}
					</UnstyledButton>
				</Popover.Target>
				<Popover.Dropdown>
					<Text span fz="0.813rem" fw={400} lh="1.25rem" c="dark.0">
						{t(
							failed
								? 'dealstack_ai.ingest.processing.error_lead'
								: stage.descriptionKey
						)}
					</Text>
					{!failed && details}
				</Popover.Dropdown>
			</Popover>
			{stage.progress !== null && (
				<Group gap="xs" wrap="nowrap" h="16px" pr="xs" pl={TITLE_INSET}>
					<Box
						flex={1}
						h="2px"
						bg="gray.2"
						style={{ overflow: 'hidden', borderRadius: '999px' }}
						role="progressbar"
						aria-label={t(stage.titleKey)}
						aria-valuemin={0}
						aria-valuemax={100}
						aria-valuenow={Math.round(stage.progress * 100)}
					>
						<Box
							h="100%"
							w="100%"
							bg="blue.6"
							style={{
								transformOrigin: 'left center',
								transform: `scaleX(${stage.progress})`,
							}}
						/>
					</Box>
					{rate && (
						<Text
							span
							fz="sm"
							c="dark.0"
							flex="none"
							styles={{ root: DS_AI_TABULAR_FIGURES }}
						>
							{rate}
						</Text>
					)}
				</Group>
			)}
			{expanded && (
				<Stack
					gap={6}
					h="118px"
					pr="xs"
					pb={10}
					pl={TITLE_INSET}
					style={{ overflow: 'auto' }}
				>
					<Text span fz="0.813rem" fw={400} lh="1.25rem" c="dark.0">
						{t(
							failed
								? 'dealstack_ai.ingest.processing.error_lead'
								: stage.descriptionKey
						)}
					</Text>
					{!failed && details}
				</Stack>
			)}
		</StageConnector>
	);
};
/** Lines detail up with the stage title: the row's padding, the icon and the gap after it (the design's 34px). */
const TITLE_INSET = `calc(8px + ${PROCESSING_ICON_SIZE} + 10px)`;

/** A stage's tick, drawn as the stage completes. */
const StageTick = styled(Box)<StyledMantineProps<'span'>>({
	animation: `${tickIn} ${DS_AI_MOTION.tickMs}ms ${DS_AI_MOTION.easeOut} both`,
});

/** The rail joining consecutive stages; it turns on completion. */
const StageConnector = styled(Box)<StyledMantineProps>({
	'&:not(:last-child)::before': {
		content: "''",
		position: 'absolute',
		left: '15px',
		top: '28px',
		bottom: '-6px',
		width: '1px',
		background: colors.gray[2],
	},
	"&[data-state='done']::before": { background: colors.teal[3] },
});
